import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    TextField,
    Button,
    Paper,
    Snackbar
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import TopSection from '../../../CommonComponents/TopSection';
import DetailsPageLowerLayer from "../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import { useAccessToken } from "../../../auth/useAccessToken";
import { addDelegation, getDelegations, deleteDelegation } from "../../../services/UserRole/ApprovalDelegationService";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const RequestApprovalDelegation = () => {
    const breadcrumbArray = ["User Access Management", "Approval Delegation"];
    const [email, setEmail] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [delegations, setDelegations] = useState([]);
    const [loadingDelegations, setLoadingDelegations] = useState(false);
    const [error, setError] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    useAccessToken();
    const loggedInEmail = useSelector((state) => state.user.email);

    useEffect(() => {
        if (!loggedInEmail) return;

        const fetchDelegations = async () => {
            try {
                setLoadingDelegations(true);
                const data = await getDelegations(loggedInEmail);

                const mapped = data?.map((item) => ({
                    id: item?.seqId,
                    email: item?.delegatedTo,
                    startDate: item?.startDate,
                    endDate: item?.endDate,
                }));
                setDelegations(mapped);
            } catch (err) {
                console.error("Failed to fetch delegations", err);
                setError("Failed to load delegations");
                setOpenSnackbar(true);
            } finally {
                setLoadingDelegations(false);
            }
        };
        fetchDelegations();
    }, [loggedInEmail]);

    const handleAddDelegation = async () => {

        // if (!delegation.startDate || !delegation.endDate) {
        //     setError("Please fill all fields.");
        //     setOpenSnackbar(true);
        //     return;
        // }
        if (delegations.length > 0) {
            setSnackbarMessage("Only one active delegation is possible at a time.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
            return;
        }
        const payload = {
            userEmail: loggedInEmail,
            delegatedTo: email,
            startDate,
            endDate,
        };
        try {
            const res = await addDelegation(payload);

            if (res?.statusCode === 201) {
                const data = await getDelegations(loggedInEmail);
                const mapped = data?.map((item) => ({
                    id: item.seqId,
                    email: item.delegatedTo,
                    startDate: item.startDate,
                    endDate: item.endDate,
                }));

                setDelegations(mapped);
                setEmail("");
                setStartDate("");
                setEndDate("");
                setSnackbarMessage(res.message || "Delegation created successfully");
                setSnackbarSeverity("success");
                setOpenSnackbar(true);
            } else {
                setSnackbarMessage(res?.message || "Failed to create delegation");
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
            }
        } catch (err) {
            console.error("Failed to create delegation", err);
            setSnackbarMessage(
                err?.response?.data?.message || "Failed to create delegation"
            );
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const handleDeleteDelegation = async (row) => {
        try {
            const res = await deleteDelegation(row.id);

            if (res?.statusCode === 200) {
                setDelegations((prev) =>                                     // Remove row from UI
                    prev.filter((d) => d.id !== row.id)
                );
                setSnackbarMessage(res.message || "Delegation deleted successfully");
                setSnackbarSeverity("success");
            } else {
                setSnackbarMessage(res?.message || "Failed to delete delegation");
                setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
        } catch (err) {
            console.error("Failed to delete delegation", err);
            setSnackbarMessage(
                err?.response?.data?.message || "Failed to delete delegation"
            );
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const delegationTab = useMemo(
        () => ({
            label: "Delegation",
            loading: loadingDelegations,
            columns: [
                { field: "email", headerName: "Delegated To", flex: 1 },
                { field: "startDate", headerName: "Start Date", flex: 1 },
                { field: "endDate", headerName: "End Date", flex: 1 },
                {
                    field: "actions",
                    headerName: "Action",
                    flex: 0.5,
                    sortable: false,
                    filterable: false,
                    cellRenderer: (params) => (
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleDeleteDelegation(params.data)}
                        >
                            Delete
                        </Button>
                    ),
                }
            ],
            rows: delegations
        }),
        [delegations]
    );

    const tabs = [delegationTab];

    return (
        // <Box
        //     sx={{
        //         display: "flex",
        //         minHeight: "100vh",
        //         bgcolor: "#b7b18bff",
        //         p: 0.5,
        //         fontFamily: "Inter",
        //     }}
        // >
        //     <Box sx={{ flexGrow: 1, mx: 0.5, mt: 0.5 }}>
        //         <Typography variant="h6" fontWeight={600} mb={0.5}>
        //             Request Approval Delegation
        //         </Typography>

        //         {/* ✅ Form stays visible */}
        //         <Paper sx={{ p: 1.5, mb: 1 }}>
        //             <Grid container spacing={1} alignItems="center">
        //                 <Grid item xs={12} md={4}>
        //                     <TextField
        //                         fullWidth
        //                         size="small"
        //                         label="Delegate User Email"
        //                         value={email}
        //                         onChange={(e) => setEmail(e.target.value)}
        //                     />
        //                 </Grid>

        //                 <Grid item xs={6} md={2.5}>
        //                     <TextField
        //                         fullWidth
        //                         size="small"
        //                         type="date"
        //                         label="Start Date"
        //                         InputLabelProps={{ shrink: true }}
        //                         value={startDate}
        //                         onChange={(e) => setStartDate(e.target.value)}
        //                     />
        //                 </Grid>

        //                 <Grid item xs={6} md={2.5}>
        //                     <TextField
        //                         fullWidth
        //                         size="small"
        //                         type="date"
        //                         label="End Date"
        //                         InputLabelProps={{ shrink: true }}
        //                         value={endDate}
        //                         onChange={(e) => setEndDate(e.target.value)}
        //                     />
        //                 </Grid>

        //                 {/* ✅ Add button retained */}
        //                 <Grid item xs={12}>
        //                     <Box
        //                         sx={{
        //                             display: "flex",
        //                             alignItems: "center",
        //                             width: "100%",
        //                         }}
        //                     >
        //                         <Button
        //                             size="small"
        //                             variant="contained"
        //                             onClick={handleAddDelegation}
        //                         >
        //                             Add
        //                         </Button>

        //                         <Button
        //                             size="small"
        //                             variant="outlined"
        //                             color="error"
        //                             onClick={handleDelete}
        //                             sx={{ ml: 55 }} // ✅ pushes delete to extreme right
        //                         >
        //                             Delete
        //                         </Button>
        //                     </Box>
        //                 </Grid>
        //             </Grid>

        //             {/* ✅ Error message shown if user tries to add another */}
        //             {error && (
        //                 <Box sx={{ mt: 1 }}>
        //                     <Alert severity="warning">{error}</Alert>
        //                 </Box>
        //             )}
        //         </Paper>

        //         <Divider sx={{ mb: 1 }} />

        //         {/* ✅ Active Delegation Table */}
        //         <Paper sx={{ p: 1 }}>
        //             <DetailsPageLowerLayer
        //                 hideTabs
        //                 tabs={[
        //                     {
        //                         rows: [
        //                             {
        //                                 id: 1,
        //                                 email: delegation.email,
        //                                 startDate: delegation.startDate,
        //                                 endDate: delegation.endDate,
        //                             },
        //                         ],
        //                         columns: [
        //                             { field: "email", headerName: "DELEGATED TO", flex: 1 },
        //                             { field: "startDate", headerName: "START DATE", flex: 1 },
        //                             { field: "endDate", headerName: "END DATE", flex: 1 },
        //                         ],
        //                         loading: false,
        //                     },
        //                 ]}
        //             />
        //         </Paper>
        //     </Box>
        // </Box>
        <div>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <TopSection breadcrumbArray={breadcrumbArray} hideHeader={true} headerName={""} hideTitle={true} />
            <Paper elevation={2} sx={{ mt: 0.5, mb: 3, pt: 2.5, bgcolor: '#ddd', height: 60 }}>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
                    <TextField
                        size="small"
                        label="Delegate User Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />

                    <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />

                    <Button
                        size="small"
                        variant="contained"
                        sx={{
                            backgroundColor: "#4b4b4bff",
                            "&:hover": {
                                backgroundColor: "#2d2d2dff"
                            }
                        }}
                        onClick={handleAddDelegation}
                    >
                        Add
                    </Button>
                    {/* <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleDelete}
                    >
                        Delete
                    </Button> */}
                </Box>
            </Paper>
            <DetailsPageLowerLayer tabs={tabs} />
        </div>


    );
};

export default RequestApprovalDelegation;